import { useState, useEffect, useMemo, useCallback } from "react";
import { ventas as apiVentas, abonos as apiAbonos } from "../../lib/api";
import { formatCurrency } from "../../lib/formato";
import { useFormattedInput } from "../../hooks/useFormattedInput";
import ItemInput from "../transacciones/ItemInput";
import BuscadorCliente from "./BuscadorCliente";
import SelectorFormaPago from "./SelectorFormaPago";
import { TrashIcon, PlusIcon } from "../Iconos";

const TIPOS = [
  { valor: "contado", nombre: "Contado", pie: "paga ya" },
  { valor: "credito", nombre: "Crédito", pie: "queda debiendo" },
  { valor: "abono", nombre: "Abono", pie: "paga deuda" },
];

const filaVacia = () => ({ tempId: Date.now(), descripcion: "", cantidad: 1, precio: "" });

/**
 * Registra una venta (de contado o de crédito) o un abono, sin salir
 * de la pantalla de Movimientos.
 *
 * Una venta siempre lleva sus productos detallados y el total lo
 * calculan ellos. Un abono es al revés: solo el monto.
 */
const RegistrarMovimientoModal = ({
  fecha,
  clientesDelDia = [],
  clienteFijo = null,
  onGuardado,
  onClose,
}) => {
  // Abierto desde la ficha de un cliente, el cliente ya viene puesto y
  // no se puede cambiar: se está registrando algo de esa persona.
  const [cliente, setCliente] = useState(clienteFijo);
  const [tipo, setTipo] = useState(null);
  const [formaPago, setFormaPago] = useState("efectivo");
  const [items, setItems] = useState([filaVacia()]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const [montoAbono, montoAbonoMostrado, alCambiarMontoAbono] = useFormattedInput("", true);

  const esAbono = tipo === "abono";
  const esVenta = tipo === "contado" || tipo === "credito";

  // Tope de crédito del cliente. Sin tope, nada cambia.
  const limite =
    cliente && cliente.limite_credito !== null && cliente.limite_credito !== undefined
      ? Number(cliente.limite_credito)
      : null;
  const saldoActual = Number(cliente && cliente.saldo_pendiente) || 0;
  const disponible = limite === null ? null : Math.max(0, limite - saldoActual);

  // Al cliente de paso solo se le venden cosas de contado: no se le fía
  // a alguien que no se sabe quién es, y sin deuda no hay abono que
  // registrarle. El servidor rechaza lo demás, aquí solo se evita que
  // llegue a intentarlo.
  const esDePaso = Number(cliente?.es_generico) === 1;

  useEffect(() => {
    if (esDePaso && tipo !== "contado") setTipo("contado");
  }, [esDePaso, tipo]);

  const total = useMemo(
    () =>
      items.reduce(
        (suma, item) => suma + (parseFloat(item.cantidad) || 0) * (parseFloat(item.precio) || 0),
        0
      ),
    [items]
  );

  const itemsValidos = useMemo(
    () =>
      items.filter(
        (item) =>
          item.descripcion.trim() !== "" &&
          parseFloat(item.cantidad) > 0 &&
          parseFloat(item.precio) > 0
      ),
    [items]
  );

  // Una venta a crédito que dejaría el saldo por encima del tope no se
  // puede guardar. El servidor la rechaza igual; esto solo evita que la
  // persona termine de escribirla para enterarse al final.
  const excedeLimite =
    tipo === "credito" && limite !== null && saldoActual + total > limite + 0.005;

  const puedeGuardar =
    !!cliente &&
    !!tipo &&
    !excedeLimite &&
    (esAbono ? !isNaN(montoAbono) && montoAbono > 0 : itemsValidos.length > 0);

  const cambiarItem = useCallback((indice, campo, valor) => {
    setItems((previos) => {
      const nuevos = [...previos];
      const item = { ...nuevos[indice] };
      item[campo] = campo === "descripcion" ? valor : Math.max(0, parseFloat(valor) || 0);
      nuevos[indice] = item;
      return nuevos;
    });
  }, []);

  const agregarItem = () => setItems((previos) => [...previos, filaVacia()]);

  const quitarItem = (indice) =>
    setItems((previos) => previos.filter((_, i) => i !== indice));

  const guardar = async (e) => {
    e.preventDefault();
    if (!puedeGuardar || guardando) return;

    setGuardando(true);
    setError(null);
    try {
      if (esAbono) {
        await apiAbonos.crear({
          cliente_id: cliente.id,
          monto: montoAbono,
          metodo_pago: formaPago,
          fecha,
        });
      } else {
        await apiVentas.crear({
          cliente_id: cliente.id,
          productos: itemsValidos,
          tipo_pago: tipo,
          // El servidor ignora la forma de pago en las ventas a crédito.
          metodo_pago: tipo === "contado" ? formaPago : null,
          fecha,
        });
      }
      onGuardado();
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-movimiento" onClick={(e) => e.stopPropagation()}>
        <h2>Registrar movimiento</h2>

        <form onSubmit={guardar} className="modal-form">
          {cliente ? (
            <div className="form-group">
              <label className="form-label">Cliente</label>
              <div className="cliente-elegido">
                <span>{cliente.nombre_completo}</span>
                {!clienteFijo && (
                  <button type="button" className="btn-enlace" onClick={() => setCliente(null)}>
                    cambiar
                  </button>
                )}
              </div>
              {esDePaso && (
                <span className="pista">
                  Venta de mostrador, solo de contado. No genera deuda ni queda en la
                  lista de clientes.
                </span>
              )}
            </div>
          ) : (
            <BuscadorCliente clientesDelDia={clientesDelDia} onElegir={setCliente} />
          )}

          <div className="form-group">
            <label className="form-label">Tipo de movimiento</label>
            <div className="tipos-movimiento">
              {TIPOS.map((opcion) => (
                <button
                  key={opcion.valor}
                  type="button"
                  className={`tipo-movimiento t-${opcion.valor}`}
                  aria-pressed={tipo === opcion.valor}
                  disabled={esDePaso && opcion.valor !== "contado"}
                  title={
                    esDePaso && opcion.valor !== "contado"
                      ? "El cliente de paso solo admite ventas de contado."
                      : undefined
                  }
                  onClick={() => setTipo(opcion.valor)}
                >
                  {opcion.nombre}
                  <small>{opcion.pie}</small>
                </button>
              ))}
            </div>
          </div>

          {(tipo === "contado" || esAbono) && (
            <SelectorFormaPago valor={formaPago} onChange={setFormaPago} />
          )}

          {esVenta && (
            <div className="form-group">
              <label className="form-label">Productos</label>
              <div className="venta-items-container">
                {items.map((item, indice) => (
                  <div className="venta-item-row" key={item.id || item.tempId}>
                    <ItemInput
                      descripcion={item.descripcion}
                      cantidad={item.cantidad}
                      precio={item.precio}
                      index={indice}
                      handleItemChange={cambiarItem}
                    />
                    {items.length > 1 && (
                      <button
                        type="button"
                        className="remove-item-btn"
                        onClick={() => quitarItem(indice)}
                        title="Quitar producto"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" className="add-item-btn" onClick={agregarItem}>
                <PlusIcon /> Agregar producto
              </button>
            </div>
          )}

          {tipo === "credito" && limite !== null && (
            <p className={`aviso-limite ${excedeLimite ? "sin-cupo" : ""}`}>
              {excedeLimite ? (
                <>
                  Con esta venta el saldo llegaría a{" "}
                  <strong>{formatCurrency(saldoActual + total)}</strong> y el límite de crédito
                  es <strong>{formatCurrency(limite)}</strong>. Regístrela de contado, o
                  pídale un abono antes.
                </>
              ) : (
                <>
                  Límite de crédito <strong>{formatCurrency(limite)}</strong> · puede llevarse
                  hasta <strong>{formatCurrency(disponible)}</strong> más a crédito
                </>
              )}
            </p>
          )}

          {esAbono ? (
            <div className="form-group">
              <label className="form-label" htmlFor="monto-abono">
                Monto del abono
              </label>
              <input
                id="monto-abono"
                type="text"
                placeholder="0"
                value={montoAbonoMostrado}
                onChange={alCambiarMontoAbono}
                className="input-monto"
                inputMode="decimal"
              />
            </div>
          ) : (
            esVenta && (
              <div className="total-calculado">
                <span>Total de la venta</span>
                <strong>{formatCurrency(total)}</strong>
              </div>
            )
          )}

          {error && <p className="error-message">{error}</p>}

          <div className="modal-actions">
            <div className="modal-actions-buttons">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn-submit" disabled={!puedeGuardar || guardando}>
                {guardando ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrarMovimientoModal;
